import { useRefreshTick } from "../lib/useAutoRefresh";
import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useClientPerms } from "../lib/useClientPerms";
import { toast } from "sonner";
import { Clock, ChevronLeft, ChevronRight, Eraser, CalendarDays, ListPlus, Send, LockOpen } from "lucide-react";
import CompanyBreadcrumb from "../components/CompanyBreadcrumb";
import { TimeLeavePanel } from "../components/TimeLeavePanel";
import TimeDayDetailModal from "../components/TimeDayDetailModal";
import TimeClockPanel from "../components/TimeClockPanel";
import TimeApprovalsPanel, { StatusBadge } from "../components/TimeApprovalsPanel";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
// wsd: 0 = Monday … 6 = Sunday (company "Timesheet week starts on" setting)
function lastWeekStart(wsd = 0) { const d = new Date(); d.setDate(d.getDate() - 7); return weekStartOf(d, wsd); }
function weekStartOf(d, wsd = 0) { const dt = new Date(d); const day = (dt.getDay() + 6 - wsd + 7) % 7; dt.setDate(dt.getDate() - day); dt.setHours(12); return dt; }
function iso(d) { return d.toISOString().slice(0, 10); }

export default function Time() {
  const { user } = useAuth();
  const location = useLocation();
  const isClient = user?.role !== "super_admin";
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [employees, setEmployees] = useState([]);
  const [weekStart, setWeekStart] = useState(() => lastWeekStart());
  const [entries, setEntries] = useState({});
  const { canEdit: canEditArea } = useClientPerms();
  const canEdit = canEditArea("time");
  const peopleEdit = canEditArea("people");
  const [ptoMap, setPtoMap] = useState({});
  // Time & Attendance (additive): raw entries per day, weekly statuses, settings, tabs, day modal
  const [dayEntries, setDayEntries] = useState({});
  const [periods, setPeriods] = useState({});
  const [tsettings, setTsettings] = useState(null);
  const [codes, setCodes] = useState([]);
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get("tab") === "approvals" ? "approvals" : new URLSearchParams(window.location.search).get("tab") === "clock" ? "clock" : "timesheets"));
  const [detail, setDetail] = useState(null);
  const tracking = !!tsettings?.time_tracking_enabled;
  const wsd = Number(tsettings?.time_week_start_day) || 0;
  useEffect(() => { setWeekStart((w) => weekStartOf(w, wsd)); }, [wsd]);
  const isMaster = user?.role === "super_admin" && (user?.staff_role || "master") === "master";
  const masterUnlock = async (emp) => {
    const p = periodOf(emp);
    const reason = window.prompt(`Master override — reopen this ${p?.status || "locked"} timesheet for editing?\n\nReason (required, recorded in the audit trail):`);
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/companies/${companyId}/timesheet-periods/${emp}/${start}/master-unlock`, { comment: reason.trim() });
      toast.success("Timesheet unlocked — hours can now be changed or deleted"); load(); loadPeriods();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || apiErr(e)); }
  };

  useEffect(() => {
    api.get("/companies").then((r) => {
      const qs = new URLSearchParams(location.search).get("company");
      const cid = qs || r.data[0]?.company_id;
      setCompanyId(cid || "");
      const co = r.data.find((c) => c.company_id === cid) || r.data[0];
      setCompanyName(co?.name || "");
    });
  }, [location.search]);

  const days = [...Array(7)].map((_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return iso(d); });
  const start = days[0], end = days[6];

  const load = useCallback(() => {
    if (!companyId) return;
    api.get(`/employees?company_id=${companyId}`).then((r) => setEmployees(
      r.data.filter((e) => e.status === "active")
        .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, undefined, { sensitivity: "base" }))));
    api.get(`/companies/${companyId}/timesheets?start=${start}&end=${end}`).then((r) => {
      // Grid cell = daily total across all time types (legacy rows without a code are Regular).
      const m = {}, de = {};
      r.data.forEach((e) => {
        const k = `${e.employee_id}|${e.date}`;
        m[k] = Math.round(((parseFloat(m[k]) || 0) + (parseFloat(e.hours) || 0)) * 100) / 100;
        (de[k] = de[k] || []).push(e);
      });
      setEntries(m); setDayEntries(de);
    }).catch(() => {});
    api.get(`/companies/${companyId}/time-codes`).then((r) => setCodes(r.data.codes || [])).catch(() => {});
  }, [companyId, start, end]);
  const tick = useRefreshTick();
  useEffect(() => { load(); }, [load, tick]);
  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/time-settings`).then((r) => setTsettings(r.data.settings)).catch(() => {});
  }, [companyId]);

  const loadPeriods = useCallback(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/timesheet-periods?week_start=${start}`).then((r) => {
      const m = {}; r.data.periods.forEach((p) => { m[p.employee_id] = p; }); setPeriods(m);
    }).catch(() => {});
  }, [companyId, start]);
  useEffect(() => { if (tracking) loadPeriods(); }, [tracking, loadPeriods]);

  const periodOf = (emp) => periods[emp];
  const rowLocked = (emp) => tracking && periodOf(emp) && !periodOf(emp).editable;
  const multiCode = (emp, d) => (dayEntries[key(emp, d)] || []).some((e) => (e.code && e.code !== "REGHRS") || e.source === "time_clock");
  const submitWeek = async (emp) => {
    try {
      await api.post(`/companies/${companyId}/timesheet-periods/${emp}/${start}/submit`, { comment: "" });
      toast.success(user?.role === "super_admin" ? "Timesheet submitted & approved for payroll" : "Timesheet submitted"); loadPeriods();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "acknowledge_required" && window.confirm(`${d.message}\n\n${(d.issues || []).map((i) => i.detail).join("\n")}\n\nAcknowledge and submit?`)) {
        try { await api.post(`/companies/${companyId}/timesheet-periods/${emp}/${start}/submit`, { comment: "", acknowledge_warnings: true }); toast.success("Timesheet submitted"); loadPeriods(); } catch (e2) { toast.error(apiErr(e2)); }
      } else toast.error(d?.message || apiErr(e));
    }
  };

  const loadPto = useCallback(async () => {
    if (!companyId || employees.length === 0) return;
    const map = {};
    await Promise.all(employees.map(async (e) => {
      try {
        const { data } = await api.get(`/companies/${companyId}/employees/${e.employee_id}/timeoff`);
        (data.requests || []).filter((r) => r.status !== "denied").forEach((r) => {
          days.forEach((d) => { if (d >= r.start_date && d <= r.end_date) map[`${e.employee_id}|${d}`] = { type: r.type, status: r.status }; });
        });
      } catch { /* ignore */ }
    }));
    setPtoMap(map);
  }, [companyId, employees, start, end]);
  useEffect(() => { loadPto(); }, [loadPto]);

  const quickPto = async (emp) => {
    if (!peopleEdit) { toast.error("You don't have permission to record time off"); return; }
    const type = (window.prompt("Time-off type: vacation, sick, personal, unpaid or other", "vacation") || "").trim().toLowerCase();
    if (!type) return;
    const s = window.prompt("Start date (YYYY-MM-DD)", start); if (!s) return;
    const en = window.prompt("End date (YYYY-MM-DD)", s); if (!en) return;
    const dys = parseFloat(window.prompt("Number of days", "1") || "0") || 0;
    try {
      await api.post(`/companies/${companyId}/employees/${emp.employee_id}/timeoff`, { type, start_date: s, end_date: en, days: dys, note: "Added from timesheet" });
      toast.success("Time off recorded (pending approval in People)");
      loadPto();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const key = (emp, date) => `${emp}|${date}`;
  const setCell = (emp, date, val) => setEntries((s) => ({ ...s, [key(emp, date)]: val }));
  const saveCell = async (emp, date) => {
    if (!canEdit || rowLocked(emp) || multiCode(emp, date)) return;
    const hours = parseFloat(entries[key(emp, date)] || 0) || 0;
    try { await api.post(`/companies/${companyId}/timesheets`, { employee_id: emp, date, hours }); if (tracking) loadPeriods(); loadEsa(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || apiErr(e)); load(); }
  };
  const empTotal = (emp) => days.reduce((t, d) => t + (parseFloat(entries[key(emp, d)] || 0) || 0), 0);
  const clearRow = async (emp) => {
    if (!canEdit || rowLocked(emp)) return;
    setEntries((s) => { const n = { ...s }; days.forEach((d) => { n[key(emp, d)] = ""; }); return n; });
    try {
      await Promise.all(days.map((d) => api.post(`/companies/${companyId}/timesheets`, { employee_id: emp, date: d, hours: 0 })));
      toast.success("Hours cleared for this week");
      loadEsa();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const dayTotal = (d) => employees.reduce((t, e) => t + (parseFloat(entries[key(e.employee_id, d)] || 0) || 0), 0);
  const grandTotal = employees.reduce((t, e) => t + empTotal(e.employee_id), 0);

  const [copying, setCopying] = useState(false);
  const [submittingAll, setSubmittingAll] = useState(false);
  const submitAll = async (ack = false) => {
    setSubmittingAll(true);
    try {
      const { data: r } = await api.post(`/companies/${companyId}/timesheet-periods/submit-all`, { week_start: start, acknowledge_warnings: ack });
      const needAck = (r.failed || []).filter((f) => f.code === "acknowledge_required");
      if (needAck.length && !ack && window.confirm(`${needAck.length} timesheet(s) have warnings:\n${needAck.map((f) => `• ${f.name}: ${(f.issues || []).map((i) => i.detail).join("; ")}`).join("\n")}\n\nAcknowledge and submit them too?`)) {
        setSubmittingAll(false); return submitAll(true);
      }
      const parts = [`${r.submitted.length} submitted${user?.role === "super_admin" ? " & approved" : ""}`];
      if (r.skipped.length) parts.push(`${r.skipped.length} skipped (no time / already submitted)`);
      if (r.failed.length) parts.push(`${r.failed.length} need attention`);
      (r.failed.length ? toast.warning : toast.success)(parts.join(" · "));
      loadPeriods(); loadEsa();
    } catch (e) { toast.error(apiErr(e)); } finally { setSubmittingAll(false); }
  };
  const [lieuBal, setLieuBal] = useState({});
  const [esaWarn, setEsaWarn] = useState({});
  const [leaveEnt, setLeaveEnt] = useState({});
  const loadEsa = useCallback(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/esa/time-warnings?start=${start}&end=${end}`).then((r) => setEsaWarn(r.data.warnings || {})).catch(() => {});
    api.get(`/companies/${companyId}/esa/leave-entitlements?year=${start.slice(0, 4)}`).then((r) => setLeaveEnt(r.data.entitlements || {})).catch(() => {});
  }, [companyId, start, end]);
  useEffect(() => { loadEsa(); }, [loadEsa]);
  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/leave-balances?balance_type=lieu`).then((r) => setLieuBal(r.data.balances || {})).catch(() => {});
  }, [companyId, start]);
  const copyLastWeek = async () => {
    if (!canEdit) return;
    const anyFilled = employees.some((e) => empTotal(e.employee_id) > 0);
    const overwrite = anyFilled && window.confirm("Some employees already have hours this week. Replace their hours with last week's? (Cancel = copy only employees with no hours yet)");
    if (anyFilled && !overwrite && !window.confirm("Copy last week's hours for employees with no hours yet?")) return;
    setCopying(true);
    try {
      const { data: r } = await api.post(`/companies/${companyId}/timesheets/copy-week`, { week_start: start, overwrite });
      const sk = r.skipped || {};
      const parts = [`${r.copied} entr${r.copied === 1 ? "y" : "ies"} copied from ${r.source_week}`];
      if (sk.already_filled?.length) parts.push(`${sk.already_filled.length} already had hours`);
      if (sk.locked?.length) parts.push(`${sk.locked.length} locked/approved`);
      if (!r.source_entries) toast.info("Last week has no hours to copy."); else toast.success(parts.join(" · "));
      load(); if (tracking) loadPeriods(); loadEsa();
    } catch (e) { toast.error(apiErr(e)); } finally { setCopying(false); }
  };

  const shiftWeek = (n) => { const d = new Date(weekStart); d.setDate(d.getDate() + n * 7); setWeekStart(d); };

  return (
    <div className="space-y-6" data-testid="time-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-muted-foreground flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> Time · Timesheets</div>
          <h1 className="text-3xl font-black tracking-tight mt-1">Time</h1>
          <p className="text-sm text-muted-foreground mt-1">Record daily hours worked. Totals carry into Run Payroll for hourly staff.</p>
          {tracking && (
            <div className="mt-3 flex gap-1" data-testid="time-tabs">
              {[["timesheets", "Timesheets"], ["clock", "Time Clock"], ...(canEdit ? [["approvals", "Approvals"]] : [])].map(([k, l]) => (
                <button key={k} data-testid={`time-tab-${k}`} onClick={() => setTab(k)} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border transition-colors ${tab === k ? "border-black bg-black text-white" : "border-border hover:border-black"}`}>{l}</button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <label data-testid="time-jump-wrap" title="Jump to the week of any date" className="flex items-center gap-2 h-9 px-3 border border-border hover:border-black transition-colors cursor-pointer">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-medium hidden sm:inline text-muted-foreground uppercase tracking-widest">Jump to</span>
            <input
              data-testid="time-jump-date"
              type="date"
              value={iso(weekStart)}
              onChange={(e) => { if (e.target.value) setWeekStart(weekStartOf(e.target.value + "T12:00:00", wsd)); }}
              className="text-sm bg-transparent focus:outline-none tabular-nums"
            />
          </label>
          <div className="flex items-center gap-2">
          <button data-testid="time-prev-week" onClick={() => shiftWeek(-1)} className="h-9 w-9 flex items-center justify-center border border-border hover:border-black transition-colors"><ChevronLeft className="h-4 w-4" /></button>
          <div data-testid="time-week-label" className="px-4 py-2 border border-border text-sm font-mono tabular-nums">{start} → {end}</div>
          <button data-testid="time-next-week" onClick={() => shiftWeek(1)} className="h-9 w-9 flex items-center justify-center border border-border hover:border-black transition-colors"><ChevronRight className="h-4 w-4" /></button>
          <button data-testid="time-last-week" onClick={() => setWeekStart(lastWeekStart(wsd))} className="h-9 px-3 border border-border text-xs font-medium hover:border-black transition-colors">Last week</button>
          {canEdit && tracking && (
            <button data-testid="time-submit-all" onClick={() => submitAll(false)} disabled={submittingAll || !employees.length} title="Submit every employee's timesheet for this week (staff: approved for payroll immediately)"
              className="h-9 px-3 border border-emerald-700 bg-emerald-700 text-white text-xs font-medium hover:bg-emerald-800 transition-colors disabled:opacity-50">{submittingAll ? "Submitting…" : user?.role === "super_admin" ? "Submit & Approve All" : "Submit All"}</button>
          )}
          {canEdit && (
            <button data-testid="time-copy-last-week" onClick={copyLastWeek} disabled={copying || !employees.length} title="Copy every employee's hours from the prior week into this week"
              className="h-9 px-3 border border-black bg-black text-white text-xs font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">{copying ? "Copying…" : "Copy Last Week"}</button>
          )}
          </div>
        </div>
      </div>

      {(!tracking || tab === "timesheets") && Object.keys(esaWarn).length > 0 && (
        <div data-testid="time-esa-banner" className="mb-3 border border-amber-500 bg-amber-50 px-4 py-2 text-xs text-amber-900 flex items-start gap-2">
          <span className="font-mono uppercase tracking-widest font-bold shrink-0">ESA</span>
          <span>{Object.values(esaWarn).reduce((n, w) => n + w.length, 0)} hours-of-work warning(s) this week — hover an employee's ESA badge or an amber cell for details (8 h/day, 48 h/week, 3-hour rule, 11 h rest, meal break). Advisory only; written excess-hours agreements can be recorded in Time Tracking settings.</span>
        </div>
      )}
      {tracking && tab === "clock" && <TimeClockPanel companyId={companyId} employees={employees} start={start} end={end} canEdit={canEdit} settings={tsettings} onChanged={() => { load(); loadPeriods(); }} />}
      {tracking && tab === "approvals" && <TimeApprovalsPanel companyId={companyId} employees={employees} user={user} canEdit={canEdit} onChanged={() => { load(); loadPeriods(); }} />}
      {(!tracking || tab === "timesheets") && (<>
      <div className="border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              <th className="px-4 py-3 sticky left-0 bg-white">Employee</th>
              {days.map((d, i) => (
                <th key={d} className="px-2 py-3 text-center text-xs font-bold text-black">{DOW[(i + wsd) % 7]}<div className="text-[11px] font-semibold text-black/80">{d.slice(5)}</div></th>
              ))}
              <th className="px-4 py-3 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((e) => {
              const salaried = e.pay_type === "salary";
              return (
              <tr key={e.employee_id} data-testid={`time-row-${e.employee_id}`} className={`border-b border-border last:border-0 ${salaried ? "bg-neutral-50/60" : ""}`}>
                <td className={`px-4 py-2 sticky left-0 font-medium whitespace-nowrap ${salaried ? "bg-neutral-50/60 text-muted-foreground" : "bg-white"}`}>
                  {e.first_name} {e.last_name}
                  <span className="ml-1 text-[9px] font-mono uppercase text-muted-foreground">{salaried ? "salary · fixed" : "hourly"}</span>
                  {(esaWarn[e.employee_id] || []).length > 0 && (
                    <span data-testid={`time-esa-warn-${e.employee_id}`} title={esaWarn[e.employee_id].map((w) => `• ${w.message}`).join("\n")} className="ml-2 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest border border-amber-500 bg-amber-50 text-amber-800 px-1.5 py-0.5 cursor-help">ESA ⚠ {esaWarn[e.employee_id].length}</span>
                  )}
                  {Object.values(leaveEnt[e.employee_id] || {}).some((l) => l.at_limit) && (
                    <span data-testid={`time-leave-limit-${e.employee_id}`} title={Object.values(leaveEnt[e.employee_id]).filter((l) => l.at_limit).map((l) => `${l.label}: ${l.used_days} of ${l.limit_days} ESA job-protected days used this year${l.exceeded ? " — further days are at the employer's discretion" : ""}`).join("\n")} className={`ml-2 inline-flex items-center text-[9px] font-mono uppercase tracking-widest border px-1.5 py-0.5 cursor-help ${Object.values(leaveEnt[e.employee_id]).some((l) => l.exceeded) ? "border-red-500 bg-red-50 text-red-700" : "border-neutral-400 bg-neutral-100 text-neutral-700"}`}>Leave limit</span>
                  )}
                  {(lieuBal[e.employee_id] || 0) !== 0 && <span data-testid={`time-lieu-${e.employee_id}`} title="Time-in-lieu bank (hours of paid time off earned from banked overtime)" className={`ml-2 text-[9px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${lieuBal[e.employee_id] < 0 ? "border-red-500 text-red-700 bg-red-50" : "border-emerald-600 text-emerald-700 bg-emerald-50"}`}>Lieu {lieuBal[e.employee_id]}h</span>}
                  {tracking && periodOf(e.employee_id) && <span className="ml-2"><StatusBadge status={periodOf(e.employee_id).status} attention={periodOf(e.employee_id).attention} testId={`time-status-${e.employee_id}`} /></span>}
                  {tracking && periodOf(e.employee_id)?.run_id && <span className="ml-1 text-[9px] font-mono text-muted-foreground" title={`Processed ${String(periodOf(e.employee_id).processed_at || "").slice(0, 10)}`}>{periodOf(e.employee_id).run_id}</span>}
                  {peopleEdit && <button data-testid={`time-add-pto-${e.employee_id}`} onClick={() => quickPto(e)} className="ml-2 text-[9px] font-mono uppercase tracking-widest border border-border px-1.5 py-0.5 hover:border-black transition-colors">+ Time off</button>}
                </td>
                {days.map((d) => {
                  const off = ptoMap[`${e.employee_id}|${d}`];
                  const flagged = (esaWarn[e.employee_id] || []).filter((w) => w.date === d);
                  const mc = multiCode(e.employee_id, d);
                  const locked = rowLocked(e.employee_id);
                  const openDetail = () => setDetail({ employee: e, date: d });
                  return (
                  <td key={d} className="px-1 py-1 text-center align-top">
                    <div className="relative inline-block">
                    <input data-testid={`time-cell-${e.employee_id}-${d}`} type="number" step="0.25" min="0" disabled={!canEdit || salaried || mc || locked}
                      title={flagged.length ? flagged.map((w) => w.message).join("\n") : salaried ? "Salaried employee — fixed hours" : locked ? "Timesheet is locked" : mc ? "Multiple time types — open details to edit" : undefined}
                      onDoubleClick={openDetail}
                      value={salaried ? "" : (entries[key(e.employee_id, d)] ?? "")}
                      onChange={(ev) => setCell(e.employee_id, d, ev.target.value)}
                      onBlur={() => saveCell(e.employee_id, d)}
                      className={`w-14 border px-1.5 py-1 text-sm text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-black disabled:bg-neutral-100 disabled:text-muted-foreground disabled:cursor-not-allowed ${flagged.length ? "border-amber-500 bg-amber-50" : mc ? "border-black font-medium" : "border-input"}`} />
                    {!salaried && <button data-testid={`time-detail-${e.employee_id}-${d}`} onClick={openDetail} title="Day details (time types, overtime, leave, notes)"
                      className="absolute -right-3 top-1/2 -translate-y-1/2 h-4 w-4 flex items-center justify-center text-muted-foreground hover:text-black"><ListPlus className="h-3 w-3" /></button>}
                    </div>
                    {off && <div data-testid={`time-pto-${e.employee_id}-${d}`} className={`mt-0.5 text-[8px] font-mono uppercase tracking-wide px-1 ${off.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} title={`${off.type} · ${off.status}`}>{off.type.slice(0, 4)}</div>}
                  </td>
                  );
                })}
                <td className="px-4 py-2 text-right tabular-nums font-medium" data-testid={`time-total-${e.employee_id}`}>
                  <div className="flex items-center justify-end gap-2">
                    <span className={salaried ? "text-muted-foreground" : ""}>{salaried ? "—" : empTotal(e.employee_id).toFixed(2)}</span>
                    {tracking && canEdit && !salaried && periodOf(e.employee_id)?.editable && empTotal(e.employee_id) > 0 && (
                      <button data-testid={`time-submit-${e.employee_id}`} onClick={() => submitWeek(e.employee_id)} title={user?.role === "super_admin" ? "Submit this week's timesheet — as Postings staff it is approved for payroll immediately" : "Submit this week's timesheet for approval"}
                        className="h-7 px-2 inline-flex items-center gap-1 border border-black text-[10px] font-mono uppercase hover:bg-black hover:text-white transition-colors"><Send className="h-3 w-3" /> {user?.role === "super_admin" ? "Submit & Approve" : "Submit"}</button>
                    )}
                    {tracking && isMaster && !salaried && rowLocked(e.employee_id) && (
                      <button data-testid={`time-master-unlock-${e.employee_id}`} onClick={() => masterUnlock(e.employee_id)} title="Master override: unlock this timesheet to change or delete hours"
                        className="h-7 px-2 inline-flex items-center gap-1 border border-red-600 text-red-700 text-[10px] font-mono uppercase hover:bg-red-600 hover:text-white transition-colors"><LockOpen className="h-3 w-3" /> Unlock</button>
                    )}
                    {canEdit && !salaried && !rowLocked(e.employee_id) && (
                      <button data-testid={`time-clear-${e.employee_id}`} onClick={() => clearRow(e.employee_id)} title="Clear this week's hours"
                        className="h-7 w-7 flex items-center justify-center border border-border text-muted-foreground hover:border-red-600 hover:bg-red-600 hover:text-white transition-colors">
                        <Eraser className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
            {employees.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">No active employees.</td></tr>
            )}
          </tbody>
          {employees.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-black font-medium bg-neutral-50">
                <td className="px-4 py-3 sticky left-0 bg-neutral-50">TOTAL</td>
                {days.map((d) => <td key={d} className="px-2 py-3 text-center tabular-nums">{dayTotal(d).toFixed(2)}</td>)}
                <td className="px-4 py-3 text-right tabular-nums" data-testid="time-grand-total">{grandTotal.toFixed(2)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {!canEdit && <p className="text-xs text-muted-foreground">View only — you don't have edit access to Time.</p>}
      {companyId && <TimeLeavePanel companyId={companyId} employees={employees} start={start} end={end} canEdit={canEdit} />}
      </>)}
      {detail && (
        <TimeDayDetailModal companyId={companyId} employee={detail.employee} date={detail.date} codes={codes}
          entries={(dayEntries[key(detail.employee.employee_id, detail.date)] || []).filter((x) => x.source !== "time_clock")}
          dayNote={periodOf(detail.employee.employee_id)?.day_notes?.[detail.date] || ""}
          editable={canEdit && !rowLocked(detail.employee.employee_id)}
          onClose={() => setDetail(null)} onSaved={() => { load(); if (tracking) loadPeriods(); loadEsa(); }} />
      )}
    </div>
  );
}
